import React, { ChangeEvent, FC, useCallback, useContext, useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { CustomerProductFetcher } from './OrganizationProduct';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { Trans, useTranslation } from 'react-i18next';
import { apiOrganizationProduct } from '../../resources/api/apiOrganizationProduct';
import { OrganizationContext } from '../../contexts/OrganizationContext';

interface IProps {
  open: boolean;
  closeDialog: () => void;
}

const { useUpdateOrganizationProduct } = apiOrganizationProduct;

export const CustomerProductEdit: FC<IProps> = ({ open, closeDialog }) => {
  const { organization } = useContext(OrganizationContext);
  const { data: customerProductData, onRefresh } = useContext(CustomerProductFetcher.Context);
  const handleError = useApiErrorHandler();
  const { t } = useTranslation();

  const { execute: editCustomerProduct } = useUpdateOrganizationProduct(
    organization ?? void 0,
    customerProductData?.id
  );

  const [notificationSettings, setNotificationSettings] = useState({
    notification_reneval_270_days: false,
    notification_reneval_180_days: false,
    notification_reneval_90_days: false,
    notification_reneval_60_days: false,
    notification_reneval_30_days: false,
    notification_expiring_30_days: false,
    ignore_notification_until_reneval: false,
  });
  const [dealId, setDealId] = useState<string>('');

  useEffect(() => {
    if (open) {
      onRefresh();
    }
  }, [open, onRefresh]);

  useEffect(() => {
    if (customerProductData) {
      setNotificationSettings({
        notification_reneval_270_days: customerProductData.properties.notification_reneval_270_days || false,
        notification_reneval_180_days: customerProductData.properties.notification_reneval_180_days || false,
        notification_reneval_90_days: customerProductData.properties.notification_reneval_90_days || false,
        notification_reneval_60_days: customerProductData.properties.notification_reneval_60_days || false,
        notification_reneval_30_days: customerProductData.properties.notification_reneval_30_days || false,
        notification_expiring_30_days: customerProductData.properties.notification_expiring_30_days || false,
        ignore_notification_until_reneval: customerProductData.properties.ignore_notification_until_reneval || false,
      });
      setDealId(customerProductData.dealId || '');
    }
  }, [customerProductData]);

  const handleCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNotificationSettings((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.checked,
    }));
  }, []);

  const saveChanges = useCallback(async () => {
    if (!customerProductData) return;
    try {
      await editCustomerProduct(
        notificationSettings.notification_reneval_270_days,
        notificationSettings.notification_reneval_180_days,
        notificationSettings.notification_reneval_90_days,
        notificationSettings.notification_reneval_60_days,
        notificationSettings.notification_reneval_30_days,
        notificationSettings.notification_expiring_30_days,
        notificationSettings.ignore_notification_until_reneval,
        dealId || undefined
      );
      await onRefresh();
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [editCustomerProduct, customerProductData, notificationSettings, dealId, closeDialog, handleError, onRefresh]);

  if (!customerProductData) return null;
  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>{t('customerProduct.notificationEditDialog.title')}</DialogTitle>
      <DialogContent>
        <Trans i18nKey="customerProduct.notificationEditDialog.message">
          <Typography paragraph />
        </Trans>
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_270_days}
              onChange={handleCheckboxChange}
              name="notification_reneval_270_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_270_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_180_days}
              onChange={handleCheckboxChange}
              name="notification_reneval_180_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_180_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_90_days}
              onChange={handleCheckboxChange}
              name="notification_reneval_90_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_90_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_60_days}
              onChange={handleCheckboxChange}
              name="notification_reneval_60_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_60_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_30_days}
              onChange={handleCheckboxChange}
              name="notification_reneval_30_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_30_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_expiring_30_days}
              onChange={handleCheckboxChange}
              name="notification_expiring_30_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_expiring_30_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.ignore_notification_until_reneval}
              onChange={handleCheckboxChange}
              name="ignore_notification_until_reneval"
            />
          }
          label={t('customerProduct.notificationEditDialog.ignore_notification_until_reneval')}
        />
        <Grid container sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <TextField
              label={t('customerProduct.notificationEditDialog.dealId')}
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>
        <Button variant="contained" color="primary" onClick={saveChanges}>
          {t('customerProduct.notificationEditDialog.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
